/**
 * React Toastify Patch
 * Patches react-toastify's internal functions to prevent removalReason errors
 * This patches the library at runtime to handle undefined toast objects gracefully
 */

/**
 * Patch react-toastify's internal toast management
 */
export const patchReactToastify = () => {
  if (typeof window === "undefined") return;

  try {
    // Try to access react-toastify's internal toast container
    // This is a workaround since react-toastify doesn't expose these functions directly
    
    // Patch the DOM to intercept close button clicks
    const originalAddEventListener = EventTarget.prototype.addEventListener;
    
    // Intercept click events on toast close buttons
    EventTarget.prototype.addEventListener = function(type, listener, options) {
      if (type === "click") {
        const wrappedListener = function(event) {
          try {
            // Check if this is a toast close button click
            const target = event.target;
            if (target && (
              target.closest('[data-testid="close-button"]') ||
              target.closest('.Toastify__close-button') ||
              target.classList.contains('Toastify__close-button') ||
              target.getAttribute('aria-label') === 'close'
            )) {
              // Prevent the default close behavior that causes the error
              event.stopPropagation();
              
              // Safely dismiss the toast by finding its container
              const toastElement = target.closest('.Toastify__toast');
              if (toastElement) {
                // Use a timeout to let React finish its current render cycle
                setTimeout(() => {
                  try {
                    // Try to dismiss by finding the toast ID
                    const toastId = toastElement.getAttribute('data-toast-id') || 
                                   toastElement.id;
                    
                    if (toastId) {
                      // Import toast dismiss dynamically
                      import('react-toastify').then(({ toast }) => {
                        try {
                          toast.dismiss(toastId);
                        } catch (e) {
                          // If dismiss fails, just remove the element
                          toastElement.remove();
                        }
                      });
                    } else {
                      // If no ID, just remove the element
                      toastElement.style.transition = 'opacity 0.2s';
                      toastElement.style.opacity = '0';
                      setTimeout(() => {
                        toastElement.remove();
                      }, 200);
                    }
                  } catch (e) {
                    // Silently handle any errors
                    if (process.env.NODE_ENV === "development") {
                      console.warn("Toast close error handled:", e);
                    }
                  }
                }, 0);
                
                return; // Don't call original listener
              }
            }
          } catch (e) {
            // Silently handle errors
            if (process.env.NODE_ENV === "development") {
              console.warn("Toast click handler error:", e);
            }
          }
          
          // Call original listener for non-toast clicks
          if (listener) {
            return originalAddEventListener.call(this, type, listener, options);
          }
        };
        
        return originalAddEventListener.call(this, type, wrappedListener, options);
      }
      
      return originalAddEventListener.call(this, type, listener, options);
    };
    
    // Also patch the toast container's onClick handler
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1 && node.classList && node.classList.contains('Toastify__toast')) {
            // Find close button and patch its click handler
            const closeButton = node.querySelector('.Toastify__close-button') ||
                               node.querySelector('[data-testid="close-button"]');
            
            if (closeButton) {
              // Remove existing click handlers
              const newButton = closeButton.cloneNode(true);
              closeButton.parentNode.replaceChild(newButton, closeButton);
              
              // Add safe close handler
              newButton.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                
                try {
                  const toastElement = node;
                  const toastId = toastElement.getAttribute('data-toast-id') || 
                                 toastElement.id;
                  
                  // Fade out and remove
                  toastElement.style.transition = 'opacity 0.2s, transform 0.2s';
                  toastElement.style.opacity = '0';
                  toastElement.style.transform = 'translateX(100%)';
                  
                  setTimeout(() => {
                    try {
                      if (toastId) {
                        import('react-toastify').then(({ toast }) => {
                          try {
                            toast.dismiss(toastId);
                          } catch (e) {
                            toastElement.remove();
                          }
                        });
                      } else {
                        toastElement.remove();
                      }
                    } catch (e) {
                      toastElement.remove();
                    }
                  }, 200);
                } catch (e) {
                  // Silently handle
                  if (process.env.NODE_ENV === "development") {
                    console.warn("Toast close error:", e);
                  }
                }
              }, true); // Use capture phase
            }
          }
        });
      });
    });
    
    // Observe the document for new toast elements
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    // Store observer for cleanup if needed
    window.__toastObserver = observer;
    
    if (process.env.NODE_ENV === "development") {
      console.log("React Toastify patched successfully");
    }
  } catch (error) {
    console.error("Failed to patch react-toastify:", error);
  }
};

/**
 * Initialize the patch
 */
export const initializeReactToastifyPatch = () => {
  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', patchReactToastify);
  } else {
    patchReactToastify();
  }
};

